import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { AppLayout } from '../components/layout/AppLayout';
import {
  createTransaction,
  listTransactions,
  updateTransaction,
  deleteTransaction,
  Transaction,
  TransactionCreatePayload,
  TransactionOwner,
} from '../services/transactionsService';
import { CUSTOM_OPTION_VALUE, TRANSACTION_TYPE_OPTIONS } from '../constants/transactions';
import { formatDate } from '../utils/format';

const ownerLabels: Record<TransactionOwner, string> = {
  gaurav: 'Gaurav',
  sourabh: 'Sourabh',
  company: 'Company',
};

const owners: TransactionOwner[] = ['gaurav', 'sourabh', 'company'];

const currentMonthISO = () => {
  const now = new Date();
  // Business period: 11th → 10th counts toward the starting month.
  const periodAnchor = now.getDate() < 11 ? new Date(now.getFullYear(), now.getMonth() - 1, 1) : new Date(now.getFullYear(), now.getMonth(), 1);
  return `${periodAnchor.getFullYear()}-${String(periodAnchor.getMonth() + 1).padStart(2, '0')}`;
};

export const TransactionsPage = () => {
  const { token } = useAuth();
  const { showToast } = useToast();
  const defaultType = TRANSACTION_TYPE_OPTIONS[0] ?? '';
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ month: currentMonthISO(), owner: 'all' as TransactionOwner | 'all' });
  const [createState, setCreateState] = useState<TransactionCreatePayload>({
    date: new Date().toISOString().slice(0, 10),
    month: currentMonthISO(),
    credit_amount: '',
    debit_amount: '',
    type: defaultType,
    owner: 'company',
    remarks: '',
  });
  const [monthManuallySet, setMonthManuallySet] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; transactionId: number | null }>({
    isOpen: false,
    transactionId: null,
  });

  const totals = useMemo(() => {
    return transactions.reduce(
      (acc, tx) => {
        acc.credit += Number(tx.credit_amount);
        acc.debit += Number(tx.debit_amount);
        return acc;
      },
      { credit: 0, debit: 0 },
    );
  }, [transactions]);

  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await listTransactions(token, {
          month: filters.month,
          owner: filters.owner === 'all' ? undefined : filters.owner,
        });
        setTransactions(response.data);
      } catch (err) {
        if (err instanceof Error) {
          showToast(err.message, 'error');
        } else {
          showToast('Failed to load transactions.', 'error');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters.month, filters.owner, token]);

  const handleFilterChange = (field: 'month' | 'owner', value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setCreateState({
      date: new Date().toISOString().slice(0, 10),
      month: currentMonthISO(),
      credit_amount: '',
      debit_amount: '',
      type: defaultType,
      owner: 'company',
      remarks: '',
    });
    setEditingTransaction(null);
    setMonthManuallySet(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) return;

    const credit = Number(createState.credit_amount || 0);
    const debit = Number(createState.debit_amount || 0);
    if (credit <= 0 && debit <= 0) {
      showToast('Please provide either credit or debit amount greater than zero.', 'error');
      return;
    }

    const normalizedType = (createState.type ?? '').trim();
    if (!normalizedType) {
      showToast('Select or enter a transaction type.', 'error');
      return;
    }

    setFormLoading(true);
    try {
      const payload: TransactionCreatePayload = {
        ...createState,
        type: normalizedType,
        credit_amount: credit > 0 ? credit : 0,
        debit_amount: debit > 0 ? debit : 0,
      };

      if (editingTransaction) {
        await updateTransaction(token, editingTransaction.id, payload);
        showToast('Transaction updated successfully.', 'success');
      } else {
        await createTransaction(token, payload);
        showToast('Transaction added successfully.', 'success');
      }

      resetForm();
      const response = await listTransactions(token, {
        month: filters.month,
        owner: filters.owner === 'all' ? undefined : filters.owner,
      });
      setTransactions(response.data);
    } catch (err) {
      if (err instanceof Error) {
        showToast(err.message, 'error');
      } else {
        showToast('Unable to save transaction.', 'error');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setCreateState({
      date: transaction.date,
      credit_amount: transaction.credit_amount,
      debit_amount: transaction.debit_amount,
      type: transaction.type ?? '',
      owner: transaction.owner,
      remarks: transaction.remarks ?? '',
    });
  };

  const handleDelete = (transaction: Transaction) => {
    setDeleteConfirm({ isOpen: true, transactionId: transaction.id });
  };

  const performDelete = async () => {
    if (!token || !deleteConfirm.transactionId) return;
    try {
      await deleteTransaction(token, deleteConfirm.transactionId);
      const response = await listTransactions(token, {
        month: filters.month,
        owner: filters.owner === 'all' ? undefined : filters.owner,
      });
      setTransactions(response.data);
      if (editingTransaction?.id === deleteConfirm.transactionId) {
        resetForm();
      }
      showToast('Transaction deleted successfully.', 'success');
      setDeleteConfirm({ isOpen: false, transactionId: null });
    } catch (err) {
      if (err instanceof Error) {
        showToast(err.message, 'error');
      } else {
        showToast('Unable to delete transaction.', 'error');
      }
      setDeleteConfirm({ isOpen: false, transactionId: null });
    }
  };

  const isFormTypeCustom =
    !createState.type || !TRANSACTION_TYPE_OPTIONS.includes(String(createState.type));

  return (
    <AppLayout
      title="Transactions"
      toolbar={
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="pill">
            Total credit: ₹ {totals.credit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </div>
          <div className="pill">
            Total debit: ₹ {totals.debit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </div>
        </div>
      }
    >
      <section className="card" style={{ padding: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18, alignItems: 'end' }}>
        <div style={{ flex: '0 0 220px' }}>
          <label className="form-label">Month</label>
          <input
            type="month"
            value={filters.month}
            onChange={(event) => handleFilterChange('month', event.target.value)}
            className="input"
          />
        </div>
        <div style={{ flex: '0 0 220px' }}>
          <label className="form-label">Owner</label>
          <select
            value={filters.owner}
            onChange={(event) => handleFilterChange('owner', event.target.value)}
            className="input"
          >
            <option value="all">All owners</option>
            {owners.map((owner) => (
              <option key={owner} value={owner}>
                {ownerLabels[owner]}
              </option>
            ))}
          </select>
        </div>
        </div>
      </section>

      <section className="card" style={{ padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0 }}>{editingTransaction ? 'Edit transaction' : 'Add transaction'}</h3>
          {editingTransaction && (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: 'auto', marginTop: 0 }}
              onClick={resetForm}
            >
              Cancel edit
            </button>
          )}
        </div>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 18 }}
        >
          <div className="form-group">
            <label htmlFor="tx-date">Date</label>
            <input
              id="tx-date"
              type="date"
              className="input"
              value={createState.date}
              onChange={(event) => {
                const val = event.target.value;
                setCreateState((prev) => ({
                  ...prev,
                  date: val,
                  // auto-derive month if user has not overridden
                  month: monthManuallySet ? prev.month : (() => {
                    const d = new Date(val);
                    if (Number.isNaN(d.getTime())) return prev.month;
                    const y = d.getFullYear();
                    const m = d.getMonth() + 1;
                    const day = d.getDate();
                    const acc = day < 11 ? new Date(y, m - 2 + 1, 1) : new Date(y, m - 1, 1);
                    const mm = String(acc.getMonth() + 1).padStart(2, '0');
                    return `${acc.getFullYear()}-${mm}`;
                  })(),
                }));
              }}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="tx-month">Month (accounting)</label>
            <input
              id="tx-month"
              type="month"
              className="input"
              value={createState.month ?? ''}
              onChange={(e) => {
                setMonthManuallySet(true);
                setCreateState((p) => ({ ...p, month: e.target.value }));
              }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="tx-credit">Credit (₹)</label>
            <input
              id="tx-credit"
              type="number"
              className="input"
              min="0"
              step="0.01"
              value={createState.credit_amount}
              onChange={(event) => setCreateState((prev) => ({ ...prev, credit_amount: event.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor="tx-debit">Debit (₹)</label>
            <input
              id="tx-debit"
              type="number"
              className="input"
              min="0"
              step="0.01"
              value={createState.debit_amount}
              onChange={(event) => setCreateState((prev) => ({ ...prev, debit_amount: event.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor="tx-type">Type</label>
            <select
              id="tx-type"
              className="input"
              value={isFormTypeCustom ? CUSTOM_OPTION_VALUE : String(createState.type)}
              onChange={(event) => {
                const value = event.target.value;
                if (value === CUSTOM_OPTION_VALUE) {
                  setCreateState((prev) => ({ ...prev, type: '' }));
                } else {
                  setCreateState((prev) => ({ ...prev, type: value }));
                }
              }}
            >
              {TRANSACTION_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
              <option value={CUSTOM_OPTION_VALUE}>Custom type…</option>
            </select>
            {isFormTypeCustom && (
              <input
                type="text"
                className="input"
                placeholder="Enter custom type"
                value={createState.type ?? ''}
                onChange={(event) => setCreateState((prev) => ({ ...prev, type: event.target.value }))}
              />
            )}
          </div>
          <div className="form-group">
            <label htmlFor="tx-owner">Owner</label>
            <select
              id="tx-owner"
              className="input"
              value={createState.owner}
              onChange={(event) =>
                setCreateState((prev) => ({ ...prev, owner: event.target.value as TransactionOwner }))
              }
            >
              {owners.map((owner) => (
                <option key={owner} value={owner}>
                  {ownerLabels[owner]}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="tx-remarks">Remarks</label>
            <input
              id="tx-remarks"
              type="text"
              className="input"
              placeholder="Optional notes"
              value={createState.remarks}
              onChange={(event) => setCreateState((prev) => ({ ...prev, remarks: event.target.value }))}
            />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn" type="submit" disabled={formLoading}>
              {formLoading ? (editingTransaction ? 'Updating…' : 'Saving…') : editingTransaction ? 'Update transaction' : 'Save transaction'}
            </button>
          </div>
        </form>
      </section>

      <section className="card" style={{ padding: 0 }}>
        <div className="table">
          <div className="table-head" style={{ gridTemplateColumns: '120px 100px 100px 0.7fr 100px 3fr 80px' }}>
            <span>Date</span>
            <span>Credit (₹)</span>
            <span>Debit (₹)</span>
            <span>Type</span>
            <span>Owner</span>
            <span>Remarks</span>
            <span style={{ textAlign: 'right' }}>Actions</span>
          </div>
          {loading ? (
            <div className="table-row">
              <span style={{ gridColumn: '1 / -1', textAlign: 'center' }}>Loading transactions…</span>
            </div>
          ) : transactions.length === 0 ? (
            <div className="table-row">
              <span style={{ gridColumn: '1 / -1', textAlign: 'center' }}>No transactions found.</span>
            </div>
          ) : (
            transactions.map((transaction) => (
              <div
                className="table-row"
                key={transaction.id}
                style={{ gridTemplateColumns: '120px 100px 100px 0.7fr 100px 3fr 80px', gap: '10px', alignItems: 'center' }}
              >
                <span>{formatDate(transaction.date)}</span>
                <span>{Number(transaction.credit_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span>{Number(transaction.debit_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span style={{ whiteSpace: 'nowrap' }}>{transaction.type || '—'}</span>
                <span style={{ whiteSpace: 'nowrap' }}>{ownerLabels[transaction.owner]}</span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={transaction.remarks || undefined}>{transaction.remarks || '—'}</span>
                <span style={{ justifySelf: 'flex-end', display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: 'auto', padding: '6px 12px', marginTop: 0, fontSize: '0.95rem' }}
                    onClick={() => handleEdit(transaction)}
                    aria-label="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{
                      width: 'auto',
                      padding: '6px 12px',
                      background: 'linear-gradient(135deg, #d14343, #f87171)',
                      fontSize: '0.95rem',
                    }}
                    onClick={() => handleDelete(transaction)}
                    aria-label="Delete"
                  >
                    🗑
                  </button>
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Transaction"
        message={
          deleteConfirm.transactionId
            ? `Are you sure you want to delete the transaction on ${formatDate(transactions.find((t) => t.id === deleteConfirm.transactionId)?.date || '')}? This action cannot be undone.`
            : 'Are you sure you want to delete this transaction? This action cannot be undone.'
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={performDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, transactionId: null })}
      />
    </AppLayout>
  );
};

